import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

const PIECE = {
  Cube: 1,
  Stone: 2,
  Jar: 3,
  Cone: 4,
  Metal: 5,
  Ice: 6,
  Tree: 7,
  Watermelon: 8,
  Plastic: 9,
  RoundMetal: 10,
  IronCan: 11,
} as const;

const SUPER_HARD = 2;

type Filler = ( x: number, y: number, layer: number, id: number ) => { type: number; variant?: number };

interface StressSpec
{
  name: string;
  columns: number;
  rows: number;
  layers: number;
  fill: Filler;
}

const TOP_ROW = 0;

const cone = ( id: number ) => ( { type: PIECE.Cone, variant: 3 + id % 8 } );

const cubes: Filler = ( x, y, layer, id ) => ( { type: PIECE.Cube, variant: ( id + layer ) % 3 } );

const mixed: Filler = ( x, y, layer, id ) =>
{
  if ( y === TOP_ROW && ( x + layer ) % 3 === 0 )
    return cone( id );

  switch ( ( x + y * 3 + layer * 5 ) % 8 )
  {
    case 0: return { type: PIECE.Cube, variant: 1 };
    case 1: return { type: PIECE.Stone };
    case 2: return { type: PIECE.Plastic, variant: ( id + layer ) % 8 };
    case 3: return { type: PIECE.Jar, variant: id % 4 };
    case 4: return { type: PIECE.Metal, variant: id % 7 };
    case 5: return { type: PIECE.Ice };
    case 6: return { type: PIECE.Tree };
    default: return { type: PIECE.Cube, variant: 2 };
  }
};

const everything: Filler = ( x, y, layer, id ) =>
{
  if ( y === TOP_ROW && ( x + layer ) % 3 === 1 )
    return cone( id );

  switch ( ( x * 2 + y + layer * 3 ) % 11 )
  {
    case 0: return { type: PIECE.Cube, variant: 0 };
    case 1: return { type: PIECE.Cube, variant: 1 };
    case 2: return { type: PIECE.Cube, variant: 2 };
    case 3: return { type: PIECE.Stone };
    case 4: return { type: PIECE.Plastic, variant: id % 8 };
    case 5: return { type: PIECE.Jar, variant: id % 4 };
    case 6: return { type: PIECE.Metal, variant: id % 7 };
    case 7: return { type: PIECE.RoundMetal, variant: id % 7 };
    case 8: return { type: PIECE.IronCan, variant: id % 2 };
    case 9: return { type: PIECE.Ice };
    default: return { type: PIECE.Watermelon };
  }
};

const SPECS: StressSpec[] = [
  { name: "WALL 9x6 x4",     columns: 9, rows: 6, layers: 4, fill: cubes },
  { name: "MIX 9x6 x5",      columns: 9, rows: 6, layers: 5, fill: mixed },
  { name: "TOWER 8x8 x4",    columns: 8, rows: 8, layers: 4, fill: mixed },
  { name: "DEPTH 9x5 x8",    columns: 9, rows: 5, layers: 8, fill: mixed },
  { name: "EVERYTHING 9x7 x6", columns: 9, rows: 7, layers: 6, fill: everything },
];

function buildLayer( spec: StressSpec, layer_index: number ): LevelLayer
{
  const grid: number[][] = Array.from( { length: spec.rows }, () => Array( spec.columns ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let y = 0; y < spec.rows; y++ )
  {
    for ( let x = 0; x < spec.columns; x++ )
    {
      const visual = spec.fill( x, y, layer_index, id );

      grid[y][x] = id;
      pieces.push( {
        id,
        x,
        y,
        width: 1,
        height: 1,
        type: visual.type as LevelPieceType,
        variant: visual.variant,
      } );
      id++;
    }
  }

  return { grid, pieces };
}

function buildConfig( index: number ): LevelConfig
{
  const spec = SPECS[index];

  return {
    level: StressTest.BASE_LEVEL + index,
    difficulty: SUPER_HARD as LevelConfig["difficulty"],
    layers: Array.from( { length: spec.layers }, ( _, i ) => buildLayer( spec, i ) ),
  };
}

export class StressTest
{
  public static readonly BASE_LEVEL = 90001;
  public static readonly COUNT = SPECS.length;

  public static readonly FIRE_INTERVAL = 0.35;

  private static active = false;
  private static autofire = false;
  private static index = 0;
  private static configs: ( LevelConfig | null )[] = SPECS.map( () => null );

  public static isActive(): boolean
  {
    return this.active;
  }

  public static isStressLevel( level: number ): boolean
  {
    return level >= this.BASE_LEVEL && level < this.BASE_LEVEL + this.COUNT;
  }

  public static getIndex(): number
  {
    return this.index;
  }

  public static getName( index: number = this.index ): string
  {
    return SPECS[( index + this.COUNT ) % this.COUNT].name;
  }

  public static getBlockCount( index: number = this.index ): number
  {
    const spec = SPECS[( index + this.COUNT ) % this.COUNT];
    return spec.columns * spec.rows * spec.layers;
  }

  public static currentLevel(): number
  {
    return this.BASE_LEVEL + this.index;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.index = ( index + this.COUNT ) % this.COUNT;
    return this.currentLevel();
  }

  public static advance(): number
  {
    return this.activate( this.index + 1 );
  }

  public static deactivate(): void
  {
    this.active = false;
    this.autofire = false;
  }

  public static isAutofiring(): boolean
  {
    return this.active && this.autofire;
  }

  public static setAutofire( on: boolean ): void
  {
    this.autofire = on;
  }

  public static toggleAutofire(): boolean
  {
    this.autofire = !this.autofire;
    return this.autofire;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    if ( !this.isStressLevel( level ) )
      return null;

    const index = level - this.BASE_LEVEL;

    if ( this.configs[index] === null )
      this.configs[index] = buildConfig( index );

    return this.configs[index];
  }
}
